/* ============ Præmier: tøjdyr i skrigende farver + localStorage ============
   Prizes carry an `owner` (the player name at win time) for the leaderboard,
   and a `kind`: "plush" (default, also for old saves) or "trophy". */

import { getName } from "./profile.js";

export const PLUSH_TYPES = [
  { id: "bamse",        name: "Bamse",        emoji: "🧸" },
  { id: "enhjoerning",  name: "Enhjørning",   emoji: "🦄" },
  { id: "dino",         name: "Dinosaur",     emoji: "🦖" },
  { id: "blaeksprutte", name: "Blæksprutte",  emoji: "🐙" },
  { id: "froe",         name: "Frø",          emoji: "🐸" },
  { id: "kanin",        name: "Kanin",        emoji: "🐰" },
  { id: "panda",        name: "Panda",        emoji: "🐼" },
  { id: "loeve",        name: "Løve",         emoji: "🦁" },
  { id: "elefant",      name: "Elefant",      emoji: "🐘" },
  { id: "pingvin",      name: "Pingvin",      emoji: "🐧" },
  { id: "haj",          name: "Haj",          emoji: "🦈" },
  { id: "koala",        name: "Koala",        emoji: "🐨" },
  { id: "hund",         name: "Tøjhund",      emoji: "🐶" },
  { id: "giraf",        name: "Giraf",        emoji: "🦒" },
  { id: "bjoern",       name: "Bjørn",        emoji: "🐻" },
];

/* rot/br styrer emoji-farvelægningen via CSS-filter (grayscale→sepia→hue-rotate);
   soft er den DÆMPEDE pastel, plys-renders indfarves med (PlushImg 'color'-blend) —
   neon klæder emojis, realistisk plys skal være afdæmpet (Daniel 13/7) */
export const LOUD_COLORS = [
  { id: "neonpink",   name: "Rosa",      css: "#FF2E92", rot: 270, br: 1.15, soft: "#ee6f95" },
  { id: "giftgroen",  name: "Lysegrøn",  css: "#39FF14", rot: 55,  br: 1.25, soft: "#7acb5f" },
  { id: "elblaa",     name: "Himmelblå", css: "#00CFFF", rot: 160, br: 1.15, soft: "#5fc0e8" },
  { id: "neonorange", name: "Orange",    css: "#FF7A00", rot: -15, br: 1.15, soft: "#eea04b" },
  { id: "skriggul",   name: "Solgul",    css: "#F5FF00", rot: 8,   br: 1.5,  soft: "#e5d44e" },
  { id: "neonlilla",  name: "Lavendel",  css: "#B026FF", rot: 225, br: 1.05, soft: "#b57fe8" },
  { id: "turkis",     name: "Mint",      css: "#12F3C4", rot: 110, br: 1.3,  soft: "#62d4b6" },
  { id: "knaldroed",  name: "Koralrød",  css: "#FF1F1F", rot: -45, br: 1.05, soft: "#e57560" },
];

export const plushFilter = (c) =>
  `grayscale(1) sepia(1) saturate(6) hue-rotate(${c.rot}deg) brightness(${c.br})`;


/* px = size in the prize room, pick = size in the prize picker / result card —
   the visual gap between sizes is deliberately big so you can SEE what you won.
   units = shelf slots (shelf capacity is 12): 6 small or 4 medium per shelf. */
export const SIZES = {
  lille:  { label: "Lille",      px: 26,  pick: 30, shelf: true,  units: 2 },
  mellem: { label: "Mellemstor", px: 54,  pick: 54, shelf: true,  units: 3 },
  stor:   { label: "Stor",       px: 116, pick: 84, shelf: false, units: 0 },
};

/* Weighted toward smaller sizes; `pool` restricts which sizes are possible
   (e.g. difficulty-gated: easy games can't award the big ones). */
export function randomSize(pool = ["lille", "mellem", "stor"]) {
  return weightedSize({ lille: 45, mellem: 35, stor: 20 }, pool);
}

/* Custom odds, e.g. the horse race scales the "stor" weight with opponents. */
export function weightedSize(weights, pool = Object.keys(weights)) {
  const total = pool.reduce((s, k) => s + (weights[k] || 0), 0);
  let r = Math.random() * total;
  for (const k of pool) { r -= weights[k] || 0; if (r < 0) return k; }
  return pool[pool.length - 1];
}

export const getType = (id) => PLUSH_TYPES.find((t) => t.id === id);
export const getColor = (id) => LOUD_COLORS.find((c) => c.id === id);

const KEY = "carnival.prizes";

export function loadPrizes() {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); }
  catch { return []; }
}

export function saveAllPrizes(all) {
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch {}
}

export function savePrize({ type, color, size, game }) {
  const prize = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    kind: "plush",
    type, color, size, game,
    owner: getName() || "?",
    wonAt: new Date().toISOString(),
  };
  const all = loadPrizes();
  all.push(prize);
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch {}
  return prize;
}

/* The gold trophy — top-shelf material, counted separately on the leaderboard. */
export function saveTrophy({ game }) {
  const prize = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    kind: "trophy",
    game,
    owner: getName() || "?",
    wonAt: new Date().toISOString(),
  };
  const all = loadPrizes();
  all.push(prize);
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch {}
  return prize;
}

export const isTrophy = (p) => p.kind === "trophy";
export const isCoin = (p) => p.kind === "coin";

/* ============ Bamsebørsen ============
   COIN_VALUE tøjdyr byttes til 1 guldmønt (logobamsen præget i guld).
   Mønten tæller som COIN_VALUE på leaderboardet, så totalen er UÆNDRET af
   et byt — børsen tømmer hylder, ikke tavlen. Pokaler er FREDET og kan
   aldrig byttes. */
export const COIN_VALUE = 100;

/* Hvor mange tøjdyr (ikke pokaler/mønter) ejer enheden lige nu? */
export function plushCount() {
  return loadPrizes().filter((p) => !isTrophy(p) && !isCoin(p)).length;
}

/* Aflevér COIN_VALUE tøjdyr og få 1 guldmønt. Udvælgelse: mindste størrelse
   først (lille -> mellem -> stor), ældste først inden for størrelsen — de
   nyeste/største bliver på hylderne. Returnerer mønten, eller null hvis der
   ikke er nok tøjdyr. */
export function exchangeForCoin(game = "Bamsebørsen") {
  const all = loadPrizes();
  const plush = all.filter((p) => !isTrophy(p) && !isCoin(p));
  if (plush.length < COIN_VALUE) return null;
  const order = { lille: 0, mellem: 1, stor: 2 };
  const give = [...plush]
    .sort((a, b) =>
      (order[a.size] ?? 0) - (order[b.size] ?? 0) ||
      String(a.id).localeCompare(String(b.id)))       // id starter med Date.now()
    .slice(0, COIN_VALUE);
  const giveIds = new Set(give.map((p) => p.id));
  const coin = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    kind: "coin",
    game,
    owner: getName() || "?",
    wonAt: new Date().toISOString(),
  };
  const rest = all.filter((p) => !giveIds.has(p.id));
  rest.push(coin);
  saveAllPrizes(rest);
  return coin;
}
